import { createSlice } from "@reduxjs/toolkit";

const baseUrl = process.env.REACT_APP_API_URL;
const salesBaseUrl = process.env.REACT_APP_SALES_API_URL;

const buildApiUrl = (root, path) => `${String(root || '').replace(/\/+$/, '')}${path}`;

// Track APIs that have been updated to use ID in the URL
// true = using ?id= for generalProject or ?projectID= for all others
const updatedApis = {
  generalProject: true,
  generalStation: true,
  soldToShipTo: true,
  priceBreakdown: true,
  ownersManual: true,
  hcRequirements: true,
  skidConstruction: true,
  pumpManifoldPiping: true,
  stationEnclosure: true,
  dropPipeComments: true,
  dropPipes: true,
  levelControls: true,
  fillSources: true,
  pumps: true,
  waterTreatment: true,
  inletScreen: true,
  inletStrainer: true,
  dischargeFilter: true,
  dischargeStrainer: true,
  roSystem: true,
  uvSystem: true,
  fertigation: true,
  skyHarvester: true,
  shipLoose: true,
  regulatedPower: true,
  unregulatedPower: true,
  regulatedBreakers: true,
  unregulatedBreakers: true,
  sequenceOfOperations: true,
  electricalOptions: true,
  telemetry: true,
  telemetryAccessories: true,
  selectedPumpOptionList: true,
  customOptions: true,
  warningReport: true,
  changedOptions: true,
  controlLogicOnly: true,
  pumpDetails: true,
  changeLog: true,
  changeOrderDetails: true,
  tbdValues: true,
  shipEarlyParent: true,
  shipEarlyItems: true,
  bookingSalesTarget: true,
  bookingJobDetail: true,
  activityLogChangeLog: true,
  bookingCommissionSplit: true,
  salesTerritory: true,
  engineeringTasks: true,
  engineeringTaskComments: true,
  engineeringProtectedTime: true,
  engineeringFeedback: true,
  fullJob: true,
  criticalData: true,
  panelSizing: true,
  panelSizingChangeLog: true,
  componentPolicies: true,
  globalPermissions: true,
  mechanicalReferenceLog: true,
  // Add more as they're updated
};

const initialState = {
  assistant: buildApiUrl(baseUrl, '/assistant'),
  // Production Portal API URLs
  formParent: `${baseUrl}/form-parent`,
  formFields: `${baseUrl}/form-fields`,
  formValues: `${baseUrl}/form-values`,
  generalProject: `${baseUrl}/general-project-info`,
  generalStation: `${baseUrl}/general-station-info`,
  soldToShipTo: `${baseUrl}/soldto-shipto-info`,
  priceBreakdown: `${baseUrl}/price-breakdown-info`,
  ownersManual: `${baseUrl}/owners-manual-info`,
  globalSearch: `${baseUrl}/global-search`,
  hcRequirements: `${baseUrl}/hc-requirements`,
  skidConstruction: `${baseUrl}/skid-construction`,
  pumpManifoldPiping: `${baseUrl}/pump-manifold-piping`,
  stationEnclosure: `${baseUrl}/station-enclosure`,
  changeLog: `${baseUrl}/change-log`,
  dropPipeComments: `${baseUrl}/drop-pipe-comments`,
  dropPipes: `${baseUrl}/drop-pipes`,
  levelControls: `${baseUrl}/level-controls`,
  fillSources: `${baseUrl}/fill-sources`,
  pumps: `${baseUrl}/pump-parent`,
  pumpDetails: `${baseUrl}/pump-details`,
  waterTreatment: `${baseUrl}/water-treatment-toggles`,
  inletScreen: `${baseUrl}/inlet-screen`,
  inletStrainer: `${baseUrl}/inlet-strainer`,
  dischargeStrainer: `${baseUrl}/discharge-strainer`,
  dischargeFilter: `${baseUrl}/discharge-filter`,
  roSystem: `${baseUrl}/ro-system`,
  uvSystem: `${baseUrl}/uv-system`,
  fertigation: `${baseUrl}/fertigation`,
  skyHarvester: `${baseUrl}/sky-harvester`,
  shipLoose: `${baseUrl}/ship-loose`,
  activityLog: `${baseUrl}/activity-log`,
  unregulatedPower: `${baseUrl}/unregulated-power-supply`,
  unregulatedBreakers: `${baseUrl}/unregulated-breakers`,
  regulatedPower: `${baseUrl}/regulated-power-supply`,
  regulatedBreakers: `${baseUrl}/regulated-breakers`,
  sequenceOfOperations: `${baseUrl}/sequence-of-operations`,
  electricalOptions: `${baseUrl}/electrical-options`,
  telemetry: `${baseUrl}/telemetry`,
  telemetryAccessories: `${baseUrl}/telemetry-accessories`,
  selectedPumpOptionList: `${baseUrl}/selected-pump-option-list`,
  customOptions: `${baseUrl}/custom-options`,
  warningReport: `${baseUrl}/warning-report`,
  changedOptions: `${baseUrl}/changed-options`,
  changeOrderParent: `${baseUrl}/change-order-parent`,
  changeOrderDetails: `${baseUrl}/change-order-details`,
  controlLogicOnly: `${baseUrl}/control-logic-only`,
  hoverDescriptions: `${baseUrl}/hover-descriptions`,
  globalVariables: `${baseUrl}/global-variables`,
  tbdValues: `${baseUrl}/tbd-finder`,
  jobSchedule: `${baseUrl}/job-schedule`,
  assemblySchedule: `${baseUrl}/assembly-schedule`,
  shipping: `${baseUrl}/shipping`,
  shippingChangeLog: `${baseUrl}/shipping-change-log`,
  npSchedule: `${baseUrl}/np-schedule`,
  releaseForm: `${baseUrl}/release-form`,
  scheduleChangeLog: `${baseUrl}/schedule-change-log`,
  engineeringPto: `${baseUrl}/engineering-pto`,
  holidays: `${baseUrl}/holidays`,
  userPreferences: `${baseUrl}/user-preferences`,
  shipEarlyItems: `${baseUrl}/ship-early-items`,
  shipEarlyParent: `${baseUrl}/ship-early-parent`,
  activityLogChangeLog: `${baseUrl}/activity-log-change-log`,
  bookingSalesTarget: `${baseUrl}/booking-sales-target`,
  bookingJobDetail: `${baseUrl}/booking-job-detail`,
  bookingCommissionSplit: `${baseUrl}/booking-commission-split`,
  salesTerritory: `${baseUrl}/sales-territory`,
  iconData: `${baseUrl}/icon-data`,
  engineeringTasks: `${baseUrl}/engineering-tasks`,
  engineeringTaskComments: `${baseUrl}/engineering-task-comments`,
  engineeringProtectedTime: `${baseUrl}/engineering-protected-time`,
  engineeringFeedback: `${baseUrl}/engineering-feedback`,
  fullJob: `${baseUrl}/full-job`,
  criticalData: `${baseUrl}/critical-data`,
  dropdownValues: `${baseUrl}/dropdown-values`,
  panelSizing: `${baseUrl}/panel-sizing`,
  panelSizingChangeLog: `${baseUrl}/panel-sizing-change-log`,
  componentPolicies: `${baseUrl}/global-permissions`,
  globalPermissions: `${baseUrl}/global-permissions`,
  mechanicalReferenceLog: `${baseUrl}/mechanical-reference-log`,
  generalProjectInfoEnhanced: `${baseUrl}/general-project-info-enhanced`,
  fullSchedule: `${baseUrl}/full-schedule`,
  bookingLogFull: `${baseUrl}/booking-log-full`,
  shippedProjects: `${baseUrl}/shipped-projects`,
  activityLogHours: `${baseUrl}/activity-log-hours`,
  npEnhanced: `${baseUrl}/np-enhanced`,
  assemblyIssuesComments: `${baseUrl}/assembly-issues-comments`,
  assemblyScheduleChangeLog: `${baseUrl}/assembly-schedule-change-log`,
  paylocity: `${baseUrl}/paylocity`,


  // Sales Portal API URLs
  spAuth: buildApiUrl(salesBaseUrl, '/sp-auth'),
  spUsers: buildApiUrl(salesBaseUrl, '/sp-users'),
  spRoles: buildApiUrl(salesBaseUrl, '/sp-roles'),
  spUserRoles: buildApiUrl(salesBaseUrl, '/sp-user-roles'),
  spSaleRepresentatives: buildApiUrl(salesBaseUrl, '/sp-sale-representatives'),
  spCompanies: buildApiUrl(salesBaseUrl, '/sp-companies'),
  spContacts: buildApiUrl(salesBaseUrl, '/sp-contacts'),
  spCustomers: buildApiUrl(salesBaseUrl, '/sp-customers'),
  spProjects: buildApiUrl(salesBaseUrl, '/sp-projects'),
  spSaleQuotes: buildApiUrl(salesBaseUrl, '/sp-sale-quotes'),
  spSalesQuoteOrder: buildApiUrl(salesBaseUrl, '/sp-sales-quote-order'),
  spSaleQuoteWarnings: buildApiUrl(salesBaseUrl, '/sp-sale-quote-warnings'),
  spSelectedOptions: buildApiUrl(salesBaseUrl, '/sp-selected-options'),
  spCostSummaries: buildApiUrl(salesBaseUrl, '/sp-cost-summaries'),
  spPumps: buildApiUrl(salesBaseUrl, '/sp-pumps'),
  spPumpConfigurations: buildApiUrl(salesBaseUrl, '/sp-pump-configurations'),
  spPumpsReference: buildApiUrl(salesBaseUrl, '/sp-pumps-reference'),
  spStations: buildApiUrl(salesBaseUrl, '/sp-stations'),
  spStationTypes: buildApiUrl(salesBaseUrl, '/sp-station-types'),
  stationTypeConfigs: buildApiUrl(salesBaseUrl, '/station-type-configs'),
  spCategories: buildApiUrl(salesBaseUrl, '/sp-categories'),
  spCategoryMappings: buildApiUrl(salesBaseUrl, '/sp-category-mappings'),
  spParts: buildApiUrl(salesBaseUrl, '/sp-parts'),
  spPartMappings: buildApiUrl(salesBaseUrl, '/sp-part-mappings'),
  spLookups: buildApiUrl(salesBaseUrl, '/sp-lookups'),
  spLookupTypes: buildApiUrl(salesBaseUrl, '/sp-lookup-types'),
  spRules: buildApiUrl(salesBaseUrl, '/sp-rules'),
  spAutoPickPartsRules: buildApiUrl(salesBaseUrl, '/sp-auto-pick-parts-rules'),
  spConfigurations: buildApiUrl(salesBaseUrl, '/sp-configurations'),
  spPrimeSystems: buildApiUrl(salesBaseUrl, '/sp-prime-systems'),
  spProductFamilies: buildApiUrl(salesBaseUrl, '/sp-product-families'),
  spProductFamilyConfigurations: buildApiUrl(salesBaseUrl, '/sp-product-family-configurations'),
  spSystemConfigurations: buildApiUrl(salesBaseUrl, '/sp-system-configurations'),
  spVerticalTurbineConfigurations: buildApiUrl(salesBaseUrl, '/sp-vertical-turbine-configurations'),
  spDisconnectTable: buildApiUrl(salesBaseUrl, '/sp-disconnect-table'),
  spEnclosurePricings: buildApiUrl(salesBaseUrl, '/sp-enclosure-pricings'),
  spHpVoltageAmp: buildApiUrl(salesBaseUrl, '/sp-hp-voltage-amp'),
  spMotorFla: buildApiUrl(salesBaseUrl, '/sp-motor-fla'),
  spTermAndConditions: buildApiUrl(salesBaseUrl, '/sp-term-and-conditions'),
  spTermsHeading: buildApiUrl(salesBaseUrl, '/sp-terms-heading'),
  spMasterStationList: buildApiUrl(salesBaseUrl, '/sp-master-station-list'),
  spConfigSections: buildApiUrl(salesBaseUrl, '/sp-config-sections'),
  spConfigSubSections: buildApiUrl(salesBaseUrl, '/sp-config-sub-sections'),
  spConfigFields: buildApiUrl(salesBaseUrl, '/sp-config-fields'),
  spConfigFieldValues: buildApiUrl(salesBaseUrl, '/sp-config-field-values'),
  spConfigValueRules: buildApiUrl(salesBaseUrl, '/sp-config-value-rules'),
  spConfigCoverage: buildApiUrl(salesBaseUrl, '/sp-config-coverage'),
  spConfigSectionRules: buildApiUrl(salesBaseUrl, '/sp-config-section-rules'),
  spPumpTableConfig: buildApiUrl(salesBaseUrl, '/sp-pump-table-config'),
  spPumpTableConfigRules: buildApiUrl(salesBaseUrl, '/sp-pump-table-config-rules'),
  spCatalogParts: buildApiUrl(salesBaseUrl, '/sp-catalog-parts'),
  spPartStations: buildApiUrl(salesBaseUrl, '/sp-part-stations'),
  spPartMatchRules: buildApiUrl(salesBaseUrl, '/sp-part-match-rules'),
  spAdvancedConfigRules: buildApiUrl(salesBaseUrl, '/sp-advanced-config-rules'),
  spPricingFormulas: buildApiUrl(salesBaseUrl, '/sp-pricing-formulas'),
};

const apiUrlSlice = createSlice({
  name: "apiUrls",
  initialState: {
    urls: initialState,
    updatedApis: updatedApis
  },
  reducers: {
    setApiUrl: (state, action) => {
      const { name, url } = action.payload;
      state.urls[name] = url;
    },
    setApiAsUpdated: (state, action) => {
      const { name, updated } = action.payload;
      state.updatedApis[name] = updated;
    },
    resetApiUrls: (state) => {
      // Reset both urls and updatedApis properties to ensure complete state reset
      state.urls = initialState;
      state.updatedApis = updatedApis;
    },
  },
});

export const { setApiUrl, setApiAsUpdated, resetApiUrls } = apiUrlSlice.actions;

export const selectApiUrls = (state) => state.apiUrls.urls; 
export const selectUpdatedApis = (state) => state.apiUrls.updatedApis;

export default apiUrlSlice.reducer;
